import { useCallback } from "react";

import { faArrowLeft, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { FormProvider } from "react-hook-form";

import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { FormField } from "src/cases/caseView/components/FormField";
import { FormFieldLabel } from "src/cases/caseView/components/FormFieldLabel";
import { HintedFieldRow } from "src/cases/caseView/components/HintedFieldRow";
import { useTaskCompletion } from "src/cases/caseView/components/useTaskCompletion";
import { useTaskFieldHintActions } from "src/cases/caseView/components/useTaskFieldHintActions";
import { useTaskFormState } from "src/cases/caseView/components/useTaskFormState";
import { useTaskFormValidForCompletion } from "src/cases/caseView/hooks";
import { useIsCaseCopilotEnabled } from "src/cases/copilot/isCaseCopilotEnabled";
import { useOpenCaseCopilot } from "src/cases/copilot/useCaseCopilot";
import type { CaseDeserialized, CaseTask } from "src/cases/queries/casesTypes";
import type { CaseTemplateTaskOutput } from "src/clients/case-management";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { IconButton } from "src/design-system/Icon";

type TaskFormSidebarViewProps = {
  task: CaseTask;
  templateTask: CaseTemplateTaskOutput;
  caseData?: CaseDeserialized;
  disabled?: boolean;
  onBack: () => void;
};

export const TaskFormSidebarView: React.FC<TaskFormSidebarViewProps> = ({
  task: initialTask,
  templateTask: fallbackTemplate,
  caseData,
  disabled,
  onBack,
}) => {
  const {
    fieldHints,
    hasHints,
    isApprovingHint,
    isRejectingHint,
    handleApproveField,
    handleRejectField,
    approveAllHints,
    rejectAllHints,
  } = useTaskFieldHintActions({ caseData, task: initialTask });

  const {
    task,
    template,
    form,
    isDirty,
    isPending,
    isFormEmpty,
    canSave,
    saveForm,
    clearForm,
  } = useTaskFormState({ initialTask, fallbackTemplate, caseData, fieldHints });

  const currentFormValues = form.watch();
  const canCompleteTask = useTaskFormValidForCompletion(
    template,
    currentFormValues as Record<string, unknown>,
  );

  const {
    isCompletionPending,
    checkboxDisabled,
    handleCompleteTask,
    handleBack,
    unsavedModal,
    hintsModal,
  } = useTaskCompletion({
    task,
    caseData,
    isDirty,
    canCompleteTask,
    saveForm,
    hasHints,
    approveAllHints,
    onBack,
  });

  const copilotEnabled = useIsCaseCopilotEnabled(caseData);
  const { openCaseCopilot } = useOpenCaseCopilot();

  const caseId = caseData?.data.id;
  const handleClearForm = useCallback(async () => {
    await clearForm(hasHints && caseId ? rejectAllHints : undefined);
  }, [clearForm, hasHints, caseId, rejectAllHints]);

  if (!template.form?.fields?.length) {
    return null;
  }

  return (
    <div className="flex h-full flex-col" data-loc="task-form-sidebar">
      <div className="flex shrink-0 flex-col gap-y-3 p-4 pb-0">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="shrink-0">
              <IconButton
                dataLoc="task-form-sidebar-back"
                icon={faArrowLeft}
                size="xs"
                onClick={handleBack}
              />
            </div>

            <p className="truncate font-inter-semibold-13px text-gray-800">
              {task.title}
            </p>
          </div>
          {task.description && (
            <MarkdownRenderer content={task.description} variant="muted" />
          )}
        </div>
        <div className="border-gray-200 border-b" />
      </div>

      <div className="decideScrollbar min-h-0 flex-1 overflow-auto">
        <div className="p-4">
          <FormProvider {...form}>
            <form className="flex flex-col gap-y-3">
              {template.form.fields.map((field) => {
                const isHinted = fieldHints != null && field.key in fieldHints;
                const fieldContent = (
                  <>
                    <FormField
                      disabled={disabled}
                      field={field}
                      hinted={copilotEnabled && isHinted}
                      onUserChange={
                        isHinted
                          ? () => handleRejectField(field.key)
                          : undefined
                      }
                    />
                    {form.formState.errors?.[field.key]?.message != null && (
                      <ErrorHint>
                        {String(form.formState.errors[field.key]?.message)}
                      </ErrorHint>
                    )}
                  </>
                );

                if (!copilotEnabled) {
                  return (
                    <div key={field.key}>
                      <FormFieldLabel field={field} />
                      {fieldContent}
                    </div>
                  );
                }

                return (
                  <HintedFieldRow
                    key={field.key}
                    disabled={!!disabled || isApprovingHint || isRejectingHint}
                    field={field}
                    fieldKey={field.key}
                    isHinted={isHinted}
                    originalValue={task.formValues[field.key]}
                    onApprove={() => handleApproveField(field.key)}
                    onReject={() => {
                      const emptyValue =
                        field.type === "multi_select" ? [] : null;
                      form.setValue(
                        field.key,
                        task.formValues[field.key] ?? emptyValue,
                        { shouldDirty: true },
                      );
                      handleRejectField(field.key);
                    }}
                    onRewriteWithCopilot={() =>
                      openCaseCopilot({
                        refLabel: `${task.title} - ${field.display_name}`,
                        formKind: "task",
                        formKey: task.key,
                        fieldKey: field.key,
                      })
                    }
                  >
                    {fieldContent}
                  </HintedFieldRow>
                );
              })}
            </form>
          </FormProvider>
        </div>

        <div className="sticky bottom-0 border-gray-200 border-t bg-white px-5 py-4">
          <div className="flex items-center justify-between">
            <Button
              dataLoc="clear-form"
              isDisabled={isPending || (isFormEmpty && !isDirty)}
              variant="secondary"
              onClick={handleClearForm}
            >
              Clear form
            </Button>
            <div className="flex gap-3">
              <Button
                dataLoc="save-form"
                isDisabled={!canSave}
                isLoading={isPending}
                variant="secondary"
                onClick={() => saveForm()}
              >
                Save
              </Button>
              <Button
                dataLoc="complete-task"
                isDisabled={
                  disabled ||
                  checkboxDisabled ||
                  isCompletionPending ||
                  (isDirty && isPending)
                }
                iconLeft={task.isCompleted ? undefined : faCheck}
                variant={task.isCompleted ? "secondary" : "primary"}
                onClick={handleCompleteTask}
              >
                {task.isCompleted ? "Mark incomplete" : "Complete task"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        cancelationButtonText="Keep editing"
        confirmationButtonText="Save & Go back"
        dataLoc="unsaved-changes-modal"
        description="You've made updates to this task form. If you leave now, your changes won't be saved."
        open={unsavedModal.open}
        title="You have unsaved changes"
        onClose={unsavedModal.onClose}
        onConfirm={unsavedModal.onConfirm}
      />

      <ConfirmationModal
        cancelationButtonText="Keep editing"
        confirmationButtonText="Confirm & Complete"
        dataLoc="unreviewed-hints-modal"
        description="There are still questions you haven't reviewed. Saving this task means you've reviewed and approved Copilot's recommendations. Are you sure you want to continue?"
        open={hintsModal.open}
        title="Copilot recommendations pending your review"
        onClose={hintsModal.onClose}
        onConfirm={hintsModal.onConfirm}
      />
    </div>
  );
};
